/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { Version } from '../../Common/types/CommonTypes';
import {
  getCatalogFunctions as getCatalogFunctionsAPI,
  getKPIs as getKPIsAPI,
  addKPI as addKPIAPI,
  removeKPI as removeKPIAPI,
  updateAComplexKPI as updateAComplexKPIAPI,
} from '../services/KPIAPI';
import {
  getV2KPIs as getV2KPIsAPI,
  addV2KPIs as addV2KPIsAPI,
  removeV2KPIs as removeV2KPIsAPI,
} from '../services/V2KPIAPI';

export const GET_AVAILABLE_KPIS = 'GET_AVAILABLE_KPIS';
export const GET_AVAILABLE_KPIS_FAILURE = 'GET_AVAILABLE_KPIS_FAILURE';
export const GET_AVAILABLE_KPIS_SUCCESS = 'GET_AVAILABLE_KPIS_SUCCESS';
export const CLEAR_AVAILABLE_KPIS = 'CLEAR_AVAILABLE_KPIS';

export const GET_KPIS = 'GET_KPIS';
export const GET_KPIS_FAILURE = 'GET_KPIS_FAILURE';
export const GET_KPIS_SUCCESS = 'GET_KPIS_SUCCESS';

export const GET_V2_KPIS = 'GET_V2_KPIS';
export const GET_V2_KPIS_FAILURE = 'GET_V2_KPIS_FAILURE';
export const GET_V2_KPIS_SUCCESS = 'GET_V2_KPIS_SUCCESS';

export const ADD_V2_KPIS = 'ADD_V2_KPIS';
export const ADD_V2_KPIS_FAILURE = 'ADD_V2_KPIS_FAILURE';
export const ADD_V2_KPIS_SUCCESS = 'ADD_V2_KPIS_SUCCESS';

export const REMOVE_V2_KPIS = 'REMOVE_V2_KPIS';
export const REMOVE_V2_KPIS_FAILURE = 'REMOVE_V2_KPIS_FAILURE';
export const REMOVE_V2_KPIS_SUCCESS = 'REMOVE_V2_KPIS_SUCCESS';

export const GET_KPI_METADATA = 'GET_KPI_METADATA';
export const GET_KPI_METADATA_FAILURE = 'GET_KPI_METADATA_FAILURE';
export const GET_KPI_METADATA_SUCCESS = 'GET_KPI_METADATA_SUCCESS';

export const ADD_KPI = 'ADD_KPI';
export const ADD_KPI_FAILURE = 'ADD_KPI_FAILURE';
export const ADD_KPI_SUCCESS = 'ADD_KPI_SUCCESS';

export const REMOVE_KPI = 'REMOVE_KPI';
export const REMOVE_KPI_FAILURE = 'REMOVE_KPI_FAILURE';
export const REMOVE_KPI_SUCCESS = 'REMOVE_KPI_SUCCESS';

export const UPDATE_COMPLEX_KPI = 'UPDATE_COMPLEX_KPI';
export const UPDATE_COMPLEX_KPI_FAILURE = 'UPDATE_COMPLEX_KPI_FAILURE';
export const UPDATE_COMPLEX_KPI_SUCCESS = 'UPDATE_COMPLEX_KPI_SUCCESS';

export const SELECT_KPI_LOG = 'SELECT_KPI_LOG';
export const CLEAR_KPI_LOG = 'CLEAR_KPI_LOG';

// action
const getCatalogFunctions = () => ({
  type: GET_AVAILABLE_KPIS,
});

// action
const getCatalogFunctionsFailure = error => ({
  type: GET_AVAILABLE_KPIS_FAILURE,
  payload: error,
});

// action
const getCatalogFunctionsSuccess = storeData => ({
  type: GET_AVAILABLE_KPIS_SUCCESS,
  payload: storeData,
});

export const clearCatalogFunctions = () => ({
  type: CLEAR_AVAILABLE_KPIS,
});

// fetch available functions
export const fetchCatalogFunctions =
  (category, version = [Version.V1]) =>
  dispatch => {
    dispatch(getCatalogFunctions());
    return getCatalogFunctionsAPI(category, version)
      .then(responseJson => {
        dispatch(getCatalogFunctionsSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(getCatalogFunctionsFailure(error));
        // throw error;
      });
  };

const getKPIs = () => ({
  type: GET_KPIS,
});

const getKPIsFailure = error => ({
  type: GET_KPIS_FAILURE,
  payload: error,
});

const getKPIsSuccess = storeData => ({
  type: GET_KPIS_SUCCESS,
  payload: storeData,
});

// fetch KPIs
export const fetchKPIs = hierarchyPath => dispatch => {
  dispatch(getKPIs());
  return getKPIsAPI(hierarchyPath)
    .then(responseJson => {
      dispatch(getKPIsSuccess(responseJson || {}));
      return responseJson;
    })
    .catch(error => {
      dispatch(getKPIsFailure(error));
      // throw error;
    });
};

// V2 KPIs
const getV2KPIs = () => ({
  type: GET_V2_KPIS,
});

const getV2KPIsFailure = error => ({
  type: GET_V2_KPIS_FAILURE,
  payload: error,
});

const getV2KPIsSuccess = storeData => ({
  type: GET_V2_KPIS_SUCCESS,
  payload: storeData,
});

// fetch KPIs for V2
export const fetchV2KPIs =
  (resourceUUID, dataItemUUID) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getV2KPIs());
    const userEmailId = dataCache.get('currentUser')?.id;
    return getV2KPIsAPI(resourceUUID, dataItemUUID, userEmailId)
      .then(responseJson => {
        dispatch(getV2KPIsSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(getV2KPIsFailure(error));
        // throw error;
      });
  };

const startAddV2KPIs = () => ({
  type: ADD_V2_KPIS,
});

const addV2KPIsFailure = error => ({
  type: ADD_V2_KPIS_FAILURE,
  payload: error,
});

const addV2KPIsSuccess = storeData => ({
  type: ADD_V2_KPIS_SUCCESS,
  payload: storeData,
});

// fetch KPIs for V2
export const addV2KPIs =
  (resourceUUID, kpiFunctionsToAdd, dataItemUUID) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startAddV2KPIs());
    const userEmailId = dataCache.get('currentUser')?.id;
    return addV2KPIsAPI(resourceUUID, kpiFunctionsToAdd, dataItemUUID, userEmailId)
      .then(responseJson => {
        dispatch(addV2KPIsSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(addV2KPIsFailure(error));
        // throw error;
      });
  };

const startRemoveV2KPIs = () => ({
  type: REMOVE_V2_KPIS,
});

const removeV2KPIsFailure = error => ({
  type: REMOVE_V2_KPIS_FAILURE,
  payload: error,
});

const removeV2KPIsSuccess = storeData => ({
  type: REMOVE_V2_KPIS_SUCCESS,
  payload: storeData,
});

// fetch KPIs for V2
export const removeV2KPIs =
  (resourceUUID, kpiFunctionsToDelete, dataItemUUID) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startRemoveV2KPIs());
    const userEmailId = dataCache.get('currentUser')?.id;
    return removeV2KPIsAPI(resourceUUID, kpiFunctionsToDelete, dataItemUUID, userEmailId)
      .then(responseJson => {
        dispatch(removeV2KPIsSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(removeV2KPIsFailure(error));
        // throw error;
      });
  };

// action
const startAddKPI = () => ({
  type: ADD_KPI,
});

// action
const addKPIFailure = error => ({
  type: ADD_KPI_FAILURE,
  payload: error,
});

// action
const addKPISuccess = storeData => ({
  type: ADD_KPI_SUCCESS,
  payload: storeData,
});

// add
export const addKPI =
  (hierarchyPath, kpiFunction) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(startAddKPI());
    return addKPIAPI(hierarchyPath, kpiFunction, dataCache.get('currentUser')?.id)
      .then(responseJson => {
        dispatch(addKPISuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(addKPIFailure(error));
        throw error;
      });
  };

// action
const startRemoveKPI = () => ({
  type: REMOVE_KPI,
});

// action
const removeKPIFailure = error => ({
  type: REMOVE_KPI_FAILURE,
  payload: error,
});

// action
const removeKPISuccess = storeData => ({
  type: REMOVE_KPI_SUCCESS,
  payload: storeData,
});

// remove function set
export const removeKPI =
  (hierarchyPath, kpi) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startRemoveKPI());
    const userEmailId = dataCache.get('currentUser')?.id;

    return removeKPIAPI(hierarchyPath, kpi, userEmailId)
      .then(responseJson => {
        dispatch(removeKPISuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(removeKPIFailure(error));
        throw error;
      });
  };

// action
const updateAComplexKPI = () => ({
  type: UPDATE_COMPLEX_KPI,
});

// action
const updateAComplexKPIFailure = error => ({
  type: UPDATE_COMPLEX_KPI_FAILURE,
  payload: error,
});

// action
const updateAComplexKPISuccess = storeData => ({
  type: UPDATE_COMPLEX_KPI_SUCCESS,
  payload: storeData,
});

// update KPI
export const updateComplexKPI =
  (hierarchyPath, functionId, kpiFunction) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(updateAComplexKPI());
    const userEmailId = dataCache.get('currentUser')?.id;

    return updateAComplexKPIAPI(hierarchyPath, functionId, kpiFunction, userEmailId)
      .then(responseJson => {
        dispatch(updateAComplexKPISuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateAComplexKPIFailure(error));
        throw error;
      });
  };

// action
export const selectKPILog = kpiLog => ({
  type: SELECT_KPI_LOG,
  payload: kpiLog,
});

// action
export const clearKPILog = () => ({
  type: CLEAR_KPI_LOG,
});
